import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react'
import { useMutation } from '@apollo/client/react'
import { Logo } from '@/features/marketing/components/Logo'
import { tokenStore } from '@/lib/auth/tokenStore'
import { adoptPlatformSession } from '@/lib/auth/sessionAdoption'
import { routes } from '@/shared/constants'
import { SIGN_IN } from '../api/mutations'
import { parseLoginError } from '../helpers/authErrors'
import { AuthSplash } from '../components/AuthSplash'
import { LobbyPromo, LobbyPromoMobile } from '../components/LobbyPromo'

interface SignInData {
  signIn: { accessToken: string }
}

/**
 * Tope de espera de la sonda SSO antes de mostrar el formulario igual.
 * Con conectividad degradada (§6.6 — un fetch colgado ni resuelve ni
 * rechaza), el login NUNCA puede quedarse bloqueado en el splash: es la vía
 * de entrada alterna. Si la adopción llega tarde con sesión viva, igualmente
 * se entra a la app.
 */
const SSO_PROBE_TIMEOUT_MS = 4000

export function LoginPage() {
  const navigate = useNavigate()
  const [show, setShow] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [checkingSso, setCheckingSso] = useState(true)

  const [signIn, { loading }] = useMutation<SignInData>(SIGN_IN, {
    context: { skipAuth: true },
  })

  // Adopción de sesión SSO (§5.6): si ya hay sesión de plataforma viva
  // (cookie httpOnly del gateway), no se vuelve a pedir el login — directo a
  // la app. Si la sonda no revive nada — o no responde dentro del tope — se
  // muestra el formulario.
  useEffect(() => {
    let active = true
    const timer = setTimeout(() => {
      if (active) setCheckingSso(false)
    }, SSO_PROBE_TIMEOUT_MS)
    void adoptPlatformSession().then((token) => {
      if (!active) return
      clearTimeout(timer)
      if (token) navigate(routes.app.dashboard, { replace: true })
      else setCheckingSso(false)
    })
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)
    try {
      const { data } = await signIn({ variables: { email, password } })
      const token = data?.signIn?.accessToken
      if (!token) throw new Error('Respuesta inesperada del servidor')
      tokenStore.set(token)
      navigate(routes.app.dashboard, { replace: true })
    } catch (err) {
      setErrorMsg(parseLoginError(err))
    }
  }

  if (checkingSso) {
    return <AuthSplash message="Verificando sesión…" />
  }

  return (
    <div className="min-h-screen bg-neutral-50 grid lg:grid-cols-2 relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(40%_40%_at_20%_20%,rgba(217,96,46,0.12),transparent_70%)]" />

      <div className="flex flex-col p-6 lg:p-12">
        <Link to="/" className="self-start"><Logo /></Link>
        <div className="flex-1 flex items-center justify-center py-10">
          <div className="w-full max-w-md">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Bienvenido</span>
            <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mt-2 mb-2">Iniciar sesión</h1>
            <p className="text-muted-foreground mb-8">Continúa con tu cuenta TecoPOS.</p>

            <form onSubmit={handleSubmit} className="space-y-4 bg-white/70 backdrop-blur-xl border border-black/5 p-6 rounded-3xl shadow-sm">
              <label className="block">
                <span className="text-xs font-semibold text-muted-foreground">Correo</span>
                <div className="mt-1.5 flex items-center gap-2 px-3.5 py-3 rounded-xl border border-black/10 bg-white focus-within:border-primary transition-colors">
                  <Mail className="size-4 text-muted-foreground" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@negocio.com"
                    className="flex-1 bg-transparent outline-none text-sm"
                    disabled={loading}
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-muted-foreground">Contraseña</span>
                <div className="mt-1.5 flex items-center gap-2 px-3.5 py-3 rounded-xl border border-black/10 bg-white focus-within:border-primary transition-colors">
                  <Lock className="size-4 text-muted-foreground" />
                  <input
                    type={show ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="flex-1 bg-transparent outline-none text-sm"
                    disabled={loading}
                  />
                  <button type="button" onClick={() => setShow((s) => !s)} className="text-muted-foreground">
                    {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </label>

              {errorMsg && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5">
                  {errorMsg}
                </p>
              )}

              <div className="flex items-center justify-end text-xs">
                <Link to="/recuperar" className="font-semibold text-primary hover:underline">¿Olvidaste tu contraseña?</Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-foreground text-background font-semibold rounded-xl hover:bg-primary transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Entrando…' : <><span>Entrar</span> <ArrowRight className="size-4" /></>}
              </button>
            </form>

            <p className="text-sm text-center text-muted-foreground mt-6">
              ¿Aún no tienes cuenta? <Link to="/registro" className="font-semibold text-primary">Registra tu negocio</Link>
            </p>

            {/* Carrusel del lobby en móvil (en escritorio lo muestra el aside) */}
            <LobbyPromoMobile />
          </div>
        </div>
      </div>

      <LobbyPromo />
    </div>
  )
}
